// ---------------------------------------------------------------------------
// Escolher o elemento debaixo do rato na pré-visualização.
//
// Os traços do desenho são finos (1,8 px de corte, 0,8 px de projecção):
// acertar num deles com o rato seria um jogo de pontaria. Em vez de
// duplicar cada caminho com um traço transparente mais largo (o que
// duplicaria um DOM que numa planta já tem milhares de `<path>`), faz-se
// a pontaria por AMOSTRAGEM: sonda-se o pixel do clique e, se não houver
// nada, uma coroa de pontos à volta, do mais perto para o mais longe.
// Custa uma mão-cheia de `elementFromPoint` por clique e não pesa nada
// no render.
//
// A sonda é injectada, por isso este módulo é testável em Node sem DOM.
// ---------------------------------------------------------------------------

/** O mínimo que precisamos de um nó do DOM. */
export interface PickNode {
  getAttribute: (name: string) => string | null
  parentElement: PickNode | null
}

/** Devolve o nó no ponto (coordenadas de viewport). */
export type PickProbe = (x: number, y: number) => PickNode | null

/** Raio da pontaria, em pixéis de ecrã. */
export const PICK_RADIUS_PX = 7

/** Atributo que o writer SVG escreve nos caminhos. */
export const LOCAL_ID_ATTR = 'data-local-id'

/** Deslocamentos a sondar, do centro para fora: o centro, oito direcções
 *  a meio raio e as mesmas oito ao raio todo. Ordem fixa (determinismo:
 *  o mesmo clique escolhe sempre o mesmo elemento). */
export function probeOffsets(radiusPx = PICK_RADIUS_PX): [number, number][] {
  const out: [number, number][] = [[0, 0]]
  const r = Math.max(radiusPx, 0)
  if (r <= 0) return out
  const dirs: [number, number][] = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
    [0.7071, 0.7071],
    [-0.7071, 0.7071],
    [-0.7071, -0.7071],
    [0.7071, -0.7071],
  ]
  for (const k of [0.5, 1]) {
    for (const [dx, dy] of dirs) out.push([dx * r * k, dy * r * k])
  }
  return out
}

/** Sobe pelos ascendentes até encontrar o `data-local-id`. */
export function localIdOf(node: PickNode | null): number | null {
  let cur: PickNode | null = node
  // Tecto de segurança: a pré-visualização tem meia dúzia de níveis.
  for (let i = 0; cur && i < 24; i++) {
    const raw = cur.getAttribute(LOCAL_ID_ATTR)
    if (raw !== null && raw !== '') {
      const n = Number(raw)
      if (Number.isFinite(n)) return n
    }
    cur = cur.parentElement
  }
  return null
}

/** localId do elemento desenhado no ponto (x, y), com pontaria
 *  generosa. `null` quando ali não está nada: é clique em vazio, e quem
 *  chama deve limpar a selecção. */
export function pickLocalIdAt(
  probe: PickProbe,
  x: number,
  y: number,
  radiusPx = PICK_RADIUS_PX,
): number | null {
  for (const [dx, dy] of probeOffsets(radiusPx)) {
    const id = localIdOf(probe(x + dx, y + dy))
    if (id !== null) return id
  }
  return null
}
